import { Cartesian3, Ray } from "cesium";
import { EmbankmentType, Side, } from "../../types";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { PositionReference } from "../../utilities/local-utilities";
import { PathSlopeType, } from "../mid-path/interface";
import { PathGeometry } from "../mid-path/mid-path-geometry";
import { CornerSlopeStrategy } from "../side-slope/cornor-slope-strategy";
import { SideSlopeGeometry } from "../side-slope/side-slope-geometry";
import { getSideSlopeChunk } from "../side-slope/utils";
import { CurveDataType } from "../../core/alignment/horizontal-alignment/interface";
export class TrenchGeometry {
    _id;
    _geometry;
    _pathGeometry;
    _sideSlopes;
    _slopeStrategy;
    get id() {
        return this._id;
    }
    get geometry() {
        return this._geometry;
    }
    get pathGeometry() {
        return this._pathGeometry;
    }
    constructor(id) {
        this._id = id;
        this._geometry = { indices: [], vertices: [] };
        this._sideSlopes = { left: [], right: [] };
        this._pathGeometry = new PathGeometry(this._id);
        this._slopeStrategy = new CornerSlopeStrategy();
    }
    dispose() {
        this._pathGeometry.dispose();
        this._sideSlopes.left.length = 0;
        this._sideSlopes.right.length = 0;
        this._geometry.indices.length = 0;
        this._geometry.vertices.length = 0;
    }
    getMergedGeometriesBySegmentSubType() {
        const result = [];
        const leftGeometry = {
            indices: [],
            vertices: [],
        };
        const rightGeometry = {
            indices: [],
            vertices: [],
        };
        const leftCuts = this._sideSlopes.left.filter((s) => !s.isFill);
        const rightCuts = this._sideSlopes.right.filter((s) => !s.isFill);
        for (const slope of leftCuts) {
            EarthBrainUtils.mergeIGeom(leftGeometry, slope.geometry.geometry);
        }
        for (const slope of rightCuts) {
            EarthBrainUtils.mergeIGeom(rightGeometry, slope.geometry.geometry);
        }
        if (this._pathGeometry.geometry.indices.length > 0) {
            result.push({
                id: this.id,
                side: Side.CENTER,
                geometry: this._pathGeometry.geometry,
                embankmentType: EmbankmentType.NONE,
            });
        }
        if (leftGeometry.indices.length > 0) {
            result.push({
                id: this.id,
                side: Side.LEFT,
                geometry: leftGeometry,
                embankmentType: EmbankmentType.CUT,
            });
        }
        if (rightGeometry.indices.length > 0) {
            result.push({
                id: this.id,
                side: Side.RIGHT,
                geometry: rightGeometry,
                embankmentType: EmbankmentType.CUT,
            });
        }
        this._geometry.indices.length = 0;
        this._geometry.vertices.length = 0;
        EarthBrainUtils.mergeIGeom(this._geometry, this._pathGeometry.geometry);
        EarthBrainUtils.mergeIGeom(this._geometry, leftGeometry);
        EarthBrainUtils.mergeIGeom(this._geometry, rightGeometry);
        return result;
    }
    update(alignment, trenchSetting, intersectionData) {
        this.dispose();
        const centralLinePoints = alignment.centerLinePoints.map((p) => p.localPointWithAdjustedHeight);
        const infosOfPVI = alignment.getInfoOfPointOfVerticalIntersections();
        const mustHavePoints = infosOfPVI.map((pvi) => pvi.point);
        for (const curve of alignment.curvePoints) {
            if (curve.type === CurveDataType.LINEAR) {
                mustHavePoints.push(curve.points[0]);
                mustHavePoints.push(curve.points[curve.points.length - 1]);
            }
        }
        const pathSetting = this.getPathSettingsFromTrenchSettings(trenchSetting);
        const computed = this._pathGeometry.update(centralLinePoints, mustHavePoints, pathSetting, (positions, segmentIndices) => alignment.getDirectionsAlongPositions(positions, segmentIndices));
        intersectionData.length > 0 &&
            this.buildSideGeometry(intersectionData, trenchSetting);
        return computed;
    }
    // public getPathInfoSnapshot() {
    // 	return this._pathGeometry.getPathInfoSnapshot();
    // }
    getPathSettingsFromTrenchSettings(trenchSetting) {
        const pathSetting = {
            commonSetting: trenchSetting.commonSetting.path.trenchSlopeType === PathSlopeType.CROSS
                ? {
                    interpolationPitch: trenchSetting.commonSetting.path.interpolationPitch,
                    pathSlopeType: PathSlopeType.CROSS,
                    leftSlope: trenchSetting.commonSetting.path.leftSlope,
                    leftWidth: trenchSetting.commonSetting.path.leftWidth,
                    rightSlope: trenchSetting.commonSetting.path.rightSlope,
                    rightWidth: trenchSetting.commonSetting.path.rightWidth,
                }
                : {
                    interpolationPitch: trenchSetting.commonSetting.path.interpolationPitch,
                    pathSlopeType: PathSlopeType.STRAIGHT,
                    slope: trenchSetting.commonSetting.path.slope,
                    leftWidth: trenchSetting.commonSetting.path.leftWidth,
                    rightWidth: trenchSetting.commonSetting.path.rightWidth,
                },
        };
        return pathSetting;
    }
    buildSideGeometry(intersectionData, settings) {
        const getReferencePosition = (point) => {
            const rayOrigin = new Cartesian3(point.x, point.y, point.z + 1000);
            const ray = new Ray(rayOrigin, new Cartesian3(0, 0, -1));
            const pointOnTileset = EarthBrainUtils.getRayIntesectionOnData(ray, intersectionData);
            if (pointOnTileset)
                return pointOnTileset.z <= point.z
                    ? PositionReference.ABOVE
                    : PositionReference.BELOW;
            return PositionReference.OUTSIDE;
        };
        const intersectionStrategy = (ray) => {
            const position = EarthBrainUtils.getRayIntesectionOnData(ray, intersectionData);
            return position;
        };
        //leftSide.
        let currentInfo = this._pathGeometry.pathInfos[0];
        const leftPointInfos = [];
        while (currentInfo) {
            for (const point of currentInfo.left) {
                if (leftPointInfos.length > 0) {
                    if (Cartesian3.equals(point, leftPointInfos[leftPointInfos.length - 1].position)) {
                        continue;
                    }
                }
                leftPointInfos.push({
                    position: point,
                    outwardDirection: currentInfo.direction.left,
                });
            }
            currentInfo = currentInfo.next;
        }
        const leftPathPoints = getSideSlopeChunk(leftPointInfos, getReferencePosition, false);
        for (const chunck of leftPathPoints) {
            if (!chunck.isPointAbove) {
                const sideSlopeGeometry = new SideSlopeGeometry({
                    pointInfos: chunck.pointInfos,
                    verticalDirection: new Cartesian3(0, 0, 1),
                    closeLoop: false,
                    generateShelf: settings.commonSetting.cutSetting.generateShelf,
                    intersectionStrategy,
                    settings: settings.commonSetting.cutSetting.left,
                });
                sideSlopeGeometry.constructSlope(settings.commonSetting.path.interpolationPitch, this._slopeStrategy);
                sideSlopeGeometry.buildGeometry();
                this._sideSlopes.left.push({
                    geometry: sideSlopeGeometry,
                    isFill: chunck.isPointAbove,
                });
            }
        }
        // connect chuncks
        // for (let i = 0; i < this._sideSlopes.left.length - 1; ++i) {
        //     const firstPoint =
        //         this._sideSlopes.left[i].geometry.pointInfos[this._sideSlopes.left[i].geometry.pointInfos.length - 1]
        //             .position;
        //     const nextPoint = this._sideSlopes.left[i + 1].geometry.pointInfos[0].position;
        //     const connectingPoint = new Cartesian3();
        //     Cartesian3.multiplyByScalar(Cartesian3.add(firstPoint, nextPoint, connectingPoint), 0.5, connectingPoint);
        //     this._sideSlopes.left[i].geometry.addConnectingPoint(connectingPoint, false);
        //     this._sideSlopes.left[i + 1].geometry.addConnectingPoint(connectingPoint, true);
        // }
        // rightSide
        let currentRightInfo = this._pathGeometry.pathInfos[0];
        const rightPointInfos = [];
        while (currentRightInfo) {
            for (const point of currentRightInfo.right) {
                if (rightPointInfos.length > 0) {
                    if (Cartesian3.equals(point, rightPointInfos[rightPointInfos.length - 1].position)) {
                        continue;
                    }
                }
                rightPointInfos.push({
                    position: point,
                    outwardDirection: currentRightInfo.direction.right,
                });
            }
            currentRightInfo = currentRightInfo.next;
        }
        const rightPathPoints = getSideSlopeChunk(rightPointInfos, getReferencePosition, false);
        for (const chunck of rightPathPoints) {
            if (!chunck.isPointAbove) {
                const sideSlopeGeometry = new SideSlopeGeometry({
                    pointInfos: chunck.pointInfos,
                    verticalDirection: new Cartesian3(0, 0, 1),
                    closeLoop: false,
                    generateShelf: settings.commonSetting.cutSetting.generateShelf,
                    intersectionStrategy,
                    settings: settings.commonSetting.cutSetting.right,
                });
                sideSlopeGeometry.constructSlope(settings.commonSetting.path.interpolationPitch, this._slopeStrategy);
                sideSlopeGeometry.buildGeometry();
                this._sideSlopes.right.push({
                    geometry: sideSlopeGeometry,
                    isFill: chunck.isPointAbove,
                });
            }
        }
        // connect chuncks
        // for (let i = 0; i < this._sideSlopes.right.length - 1; ++i) {
        //     const firstPoint =
        //         this._sideSlopes.right[i].geometry.pointInfos[this._sideSlopes.right[i].geometry.pointInfos.length - 1]
        //             .position;
        //     const nextPoint = this._sideSlopes.right[i + 1].geometry.pointInfos[0].position;
        //     const connectingPoint = new Cartesian3();
        //     Cartesian3.multiplyByScalar(Cartesian3.add(firstPoint, nextPoint, connectingPoint), 0.5, connectingPoint);
        //     this._sideSlopes.right[i].geometry.addConnectingPoint(connectingPoint, false);
        //     this._sideSlopes.right[i + 1].geometry.addConnectingPoint(connectingPoint, true);
        // }
    }
}
//# sourceMappingURL=trench-geometry.js.map